import React, { useState, useRef } from 'react';

// PRD-027 WU-C — the buyer-redline layer, rendered INSIDE an existing worksheet
// cell (no re-columning, no redesign). A suggestible cell (Price, Qty, or any of
// the 5 spec blocks) gets:
//   • buyer + draft: a subtle "+ Suggest" affordance (hover-revealed).
//   • an open Suggestion: a highlighter-yellow marker (both sides see it).
//   • click → compact popover: current → proposed diff + comment thread +
//     actions (seller Accept/Reject; buyer Withdraw; both comment).
// Suggestions never carry onto the Agreement, so this renders only pre-signing.

const STATUS_CLASS = {
  open: 'bg-amber-200 text-amber-900 border-amber-400',
  accepted: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  withdrawn: 'bg-gray-100 text-gray-600 border-gray-200',
  abandoned: 'bg-gray-100 text-gray-600 border-gray-200',
};

function fmtVal(target, v) {
  if (v == null || v === '') return '—';
  if (target.type === 'sell_price_per_unit') return `$${Number(v).toFixed(2)}`;
  if (target.type === 'sell_quantity') return Number(v).toLocaleString();
  return String(v);
}

export default function SuggestionControl({ target, suggestion, side, canSuggest, currentValue, handlers }) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [proposed, setProposed] = useState('');
  const [comment, setComment] = useState('');
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const anchorRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const status = suggestion?.status;
  const isOpen = status === 'open';

  const place = () => {
    const r = anchorRef.current?.getBoundingClientRect();
    if (r) setPos({ top: Math.min(r.bottom + 6, window.innerHeight - 320), left: Math.max(8, Math.min(r.left, window.innerWidth - 320)) });
  };
  const togglePopover = () => { if (!open) place(); setOpen((o) => !o); setCreating(false); };
  const startCreate = () => { place(); setCreating(true); setOpen(true); setProposed(''); setComment(''); };

  const submitCreate = async () => {
    if (!proposed.trim()) return;
    setBusy(true);
    try { await handlers.onCreate(target, proposed.trim(), comment.trim()); setOpen(false); setCreating(false); }
    finally { setBusy(false); }
  };
  const act = async (fn) => { setBusy(true); try { await fn(); setOpen(false); } finally { setBusy(false); } };
  const sendReply = async () => { if (!reply.trim()) return; setBusy(true); try { await handlers.onComment(suggestion.id, reply.trim()); setReply(''); } finally { setBusy(false); } };

  // Nothing to show: no suggestion and the caller can't suggest.
  if (!suggestion && !canSuggest) return null;

  return (
    <>
      <span ref={anchorRef} className="inline-flex">
        {suggestion ? (
          <button
            onClick={(e) => { e.stopPropagation(); togglePopover(); }}
            className={`inline-flex items-center gap-1 text-[9.5px] font-extrabold rounded px-1.5 py-0.5 border ${STATUS_CLASS[status] || STATUS_CLASS.open} ${isOpen ? 'shadow-[0_0_0_3px_rgba(251,191,36,0.18)]' : 'opacity-80'}`}
            title="Open suggestion"
          >
            {isOpen ? <><span className="w-1.5 h-1.5 rounded-full bg-amber-700 inline-block" />1</> : status}
          </button>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); startCreate(); }}
            className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center text-[9.5px] font-extrabold text-amber-700 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5 hover:bg-amber-200 hover:text-amber-900"
          >
            + Suggest
          </button>
        )}
      </span>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="fixed z-50 w-[320px] bg-white border border-amber-300 rounded-xl shadow-2xl overflow-hidden text-left" style={{ top: pos.top, left: pos.left }} onClick={(e) => e.stopPropagation()}>
            {creating ? (
              <div className="p-3">
                <div className="text-[11px] font-extrabold uppercase tracking-wide text-amber-700 mb-2">Suggest an edit</div>
                <div className="text-[11px] text-gray-600 mb-1">Current: <span className="text-gray-700 font-semibold">{fmtVal(target, currentValue)}</span></div>
                <input autoFocus value={proposed} onChange={(e) => setProposed(e.target.value)} placeholder="Proposed value"
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm mb-2" />
                <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment (optional)"
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs mb-2" />
                <div className="flex justify-end gap-2">
                  <button onClick={() => setOpen(false)} className="text-xs text-gray-600 px-2 py-1">Cancel</button>
                  <button onClick={submitCreate} disabled={busy || !proposed.trim()} className="text-xs font-bold text-amber-900 bg-amber-300 hover:bg-amber-400 rounded-md px-3 py-1">Send suggestion</button>
                </div>
              </div>
            ) : suggestion ? (
              <>
                <div className="flex justify-between items-center px-3 py-2 bg-amber-50 border-b border-amber-200">
                  <span className="text-[10px] font-extrabold uppercase tracking-wide text-amber-700">Suggested edit</span>
                  <span className={`text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded-full border ${STATUS_CLASS[status]}`}>{status}</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 text-sm tabular-nums">
                  <span className="text-gray-600 line-through">{fmtVal(target, currentValue)}</span>
                  <span className="text-amber-600 font-bold">→</span>
                  <span className="text-gray-900 font-bold">{fmtVal(target, suggestion.proposed_value)}</span>
                </div>
                <div className="max-h-[150px] overflow-auto px-3 py-1">
                  {(suggestion.comments || []).length === 0 && <p className="text-[11px] text-gray-600 py-1">No comments yet.</p>}
                  {(suggestion.comments || []).map((c) => (
                    <div key={c.id} className="py-1.5 border-t border-gray-100 first:border-t-0">
                      <span className={`text-[10px] font-extrabold uppercase ${c.author_side === 'buyer' ? 'text-emerald-700' : 'text-primary-600'}`}>{c.author_side === 'buyer' ? 'Client' : 'Agency'}</span>
                      <div className="text-xs text-gray-700">{c.body}</div>
                    </div>
                  ))}
                </div>
                {isOpen && (
                  <>
                    <div className="flex gap-1.5 px-3 py-2 border-t border-gray-100">
                      <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply in thread…"
                        className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-xs" onKeyDown={(e) => { if (e.key === 'Enter') sendReply(); }} />
                      <button onClick={sendReply} disabled={busy} className="text-xs font-semibold text-gray-600 border border-gray-300 rounded-md px-2">Comment</button>
                    </div>
                    <div className="flex gap-2 px-3 py-2 border-t border-gray-100">
                      {side === 'seller' ? (
                        <>
                          <button onClick={() => act(() => handlers.onAccept(suggestion.id))} disabled={busy} className="text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md px-3 py-1">Accept</button>
                          <button onClick={() => act(() => handlers.onReject(suggestion.id))} disabled={busy} className="text-xs font-semibold text-red-600 border border-red-200 rounded-md px-3 py-1">Reject</button>
                          <span className="text-[11px] text-gray-600 self-center">or counter in the thread</span>
                        </>
                      ) : (
                        <>
                          <button onClick={() => act(() => handlers.onWithdraw(suggestion.id))} disabled={busy} className="text-xs font-semibold text-gray-600 border border-gray-300 rounded-md px-3 py-1">Withdraw</button>
                          <span className="text-[11px] text-gray-600 self-center">waiting on the agency</span>
                        </>
                      )}
                    </div>
                  </>
                )}
              </>
            ) : null}
          </div>
        </>
      )}
    </>
  );
}
